"use client";

import { useEffect, useRef, useState } from "react";
import jsQR from "jsqr";
import { useAuth } from "./auth-context";
import { apiJson } from "./api";
import "./partner-portal.css";

type Row = Record<string, any>;
type PortalData = {
  currentPartner: Row | null;
  partnerOffers: Row[];
  uses: Row[];
  partnerUsers: Row[];
};
const empty: PortalData = {
  currentPartner: null,
  partnerOffers: [],
  uses: [],
  partnerUsers: [],
};
const menu = [
  ["overview", "⌂", "Visão geral"],
  ["validate", "▣", "Validar associado"],
  ["offers", "◇", "Produtos e descontos"],
  ["uses", "↗", "Utilizações"],
  ["reports", "▥", "Relatórios"],
  ["profile", "▤", "Perfil da empresa"],
  ["staff", "♙", "Funcionários"],
];
const br = (date?: string) =>
  date ? date.slice(0, 10).split("-").reverse().join("/") : "—";
const money = (value: number) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(
    value || 0,
  );

async function api(body?: Row) {
  const r = await fetch(
      "/api/partner-portal.php",
      body
        ? {
            method: "POST",
            headers: { "content-type": "application/json" },
            body: JSON.stringify(body),
          }
        : undefined,
    ),
    j = await apiJson(r);
  if (!r.ok)
    throw new Error(j.error || "Não foi possível concluir a operação.");
  return j;
}

export default function PartnerPortal() {
  const { user, logout } = useAuth();
  const [view, setView] = useState("overview"),
    [data, setData] = useState<PortalData>(empty),
    [loading, setLoading] = useState(true),
    [mobile, setMobile] = useState(false),
    [offer, setOffer] = useState<Row | null>(null),
    [toast, setToast] = useState("");
  useEffect(() => {
    api()
      .then(setData)
      .catch((e) => alert(e.message))
      .finally(() => setLoading(false));
  }, []);
  function saved(message: string, next?: PortalData) {
    if (next) setData(next);
    setOffer(null);
    setToast(message);
    setTimeout(() => setToast(""), 2400);
  }
  const partner = data.currentPartner,
    logo =
      partner?.name
        ?.split(" ")
        .map((x: string) => x[0])
        .slice(0, 2)
        .join("") || "AP";
  return (
    <div className="app-shell partner-shell">
      <aside className={mobile ? "sidebar open" : "sidebar"}>
        <div className="brand">
          <span className="brandmark">●●●</span>
          <div>
            <strong>
              ASSOCIA<span>PRO</span>
            </strong>
            <small>PORTAL DO PARCEIRO</small>
          </div>
        </div>
        <nav>
          {menu.map((item) => (
            <button
              key={item[0]}
              className={view === item[0] ? "active" : ""}
              onClick={() => {
                setView(item[0]);
                setMobile(false);
              }}
            >
              <i>{item[1]}</i>
              {item[2]}
            </button>
          ))}
        </nav>
        <div className="profile">
          <div className="partner-avatar">{logo}</div>
          <div>
            <b>{partner?.name || user.name}</b>
            <small>Parceiro</small>
          </div>
          <button className="logout-mini" onClick={logout}>
            ↪
          </button>
        </div>
      </aside>
      <main className="workspace">
        <header>
          <button className="hamb" onClick={() => setMobile(!mobile)}>
            ☰
          </button>
          <div className="mobile-brand">
            ASSOCIA<span>PRO</span>
          </div>
          <div className="top-actions">
            <div className="partner-avatar small">{logo}</div>
            <div className="admin">
              <b>{partner?.name || user.name}</b>
              <small>Portal do parceiro</small>
            </div>
            <button className="ghost top-logout" onClick={logout}>
              Sair
            </button>
          </div>
        </header>
        {loading ? (
          <div className="loading">
            <div />
            <p>Carregando Portal do Parceiro...</p>
          </div>
        ) : !partner ? (
          <NotLinked />
        ) : (
          <>
            {view === "overview" && <Overview data={data} go={setView} />}{" "}
            {view === "validate" && (
              <Validation data={data} update={setData} saved={saved} />
            )}{" "}
            {view === "offers" && <Offers data={data} edit={setOffer} />}{" "}
            {view === "uses" && <Uses data={data} />}{" "}
            {view === "reports" && <Reports data={data} />}{" "}
            {view === "profile" && (
              <Profile data={data} update={setData} saved={saved} />
            )}{" "}
            {view === "staff" && <Staff data={data} />}
          </>
        )}
      </main>
      {offer && (
        <OfferEditor
          offer={offer}
          close={() => setOffer(null)}
          save={async (body) =>
            saved(
              offer.id ? "Oferta atualizada" : "Oferta cadastrada",
              await api(body),
            )
          }
        />
      )}{" "}
      {toast && <div className="toast">✓ {toast}</div>}{" "}
      {mobile && <div className="shade" onClick={() => setMobile(false)} />}
    </div>
  );
}

function Title({
  title,
  sub,
  children,
}: {
  title: string;
  sub: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="page-title">
      <div>
        <h1>{title}</h1>
        <p>{sub}</p>
      </div>
      {children}
    </div>
  );
}
function NotLinked() {
  return (
    <div className="page">
      <section className="panel partner-empty">
        <div>!</div>
        <h2>Conta ainda não vinculada</h2>
        <p>
          Peça ao administrador para vincular este login ao cadastro do
          estabelecimento.
        </p>
      </section>
    </div>
  );
}
function Metric({
  icon,
  color,
  label,
  value,
  sub,
}: {
  icon: string;
  color: string;
  label: string;
  value: string;
  sub: string;
}) {
  return (
    <div className="metric">
      <div className={`metric-icon ${color}`}>{icon}</div>
      <div>
        <p>{label}</p>
        <h2>{value}</h2>
        <small>{sub}</small>
      </div>
    </div>
  );
}

function Overview({ data, go }: { data: PortalData; go: (x: string) => void }) {
  const month = new Date().toISOString().slice(0, 7),
    uses = data.uses.filter((x) => x.used_at?.startsWith(month)),
    unique = new Set(uses.map((x) => x.member_id)).size,
    discount = uses.reduce((s, x) => s + Number(x.discount_amount || 0), 0),
    counts = data.partnerOffers.map((o) => ({
      offer: o,
      total: uses.filter((x) => Number(x.offer_id) === Number(o.id)).length,
    })),
    best = [...counts].sort((a, b) => b.total - a.total)[0];
  return (
    <div className="page">
      <Title
        title={`Olá, ${data.currentPartner?.name}! 👋`}
        sub="Acompanhe suas utilizações, ofertas e resultados."
      />
      <div className="metrics dash4 partner-metrics">
        <Metric
          icon="▥"
          color="blue"
          label="Utilizações no mês"
          value={String(uses.length)}
          sub="Benefícios registrados"
        />
        <Metric
          icon="♙"
          color="green"
          label="Associados atendidos"
          value={String(unique)}
          sub="Pessoas diferentes"
        />
        <Metric
          icon="◇"
          color="amber"
          label="Descontos concedidos"
          value={money(discount)}
          sub="Economia dos associados"
        />
        <Metric
          icon="↗"
          color="purple"
          label="Oferta mais usada"
          value={best?.offer.title || "Sem dados"}
          sub={best ? `${best.total} utilizações` : "Comece validando"}
        />
      </div>
      <div className="partner-overview-grid">
        <section className="panel partner-table-panel">
          <div className="panel-head">
            <h3>Últimas utilizações</h3>
            <button className="link" onClick={() => go("uses")}>
              Ver todas
            </button>
          </div>
          <UsageTable rows={data.uses.slice(0, 6)} />
        </section>
        <section className="panel performance-card">
          <div className="panel-head">
            <h3>Desempenho das ofertas</h3>
            <button className="link" onClick={() => go("reports")}>
              Ver relatório
            </button>
          </div>
          <div className="offer-performance">
            {counts.map(({ offer, total }, i) => (
              <div key={offer.id}>
                <span className={`performance-dot p${i}`} />
                <b>{offer.title}</b>
                <em>{total} usos</em>
                <div>
                  <i style={{ width: `${Math.min(100, total * 12)}%` }} />
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
      <div className="info partner-tip">
        ⓘ Divulgue seus benefícios para aumentar as utilizações e o engajamento
        dos associados.
      </div>
    </div>
  );
}

function Validation({
  data,
  update,
  saved,
}: {
  data: PortalData;
  update: (x: PortalData) => void;
  saved: (x: string) => void;
}) {
  const [q, setQ] = useState(""),
    [result, setResult] = useState<Row | null>(null),
    [selected, setSelected] = useState(String(data.partnerOffers[0]?.id || "")),
    [busy, setBusy] = useState(false),
    fileRef = useRef<HTMLInputElement>(null);
  async function validate(value = q) {
    if (!value.trim()) return alert("Digite a carteirinha ou CPF.");
    setBusy(true);
    try {
      setResult(
        (await api({ action: "partner.validate", query: value.trim() }))
          .validation,
      );
    } catch (e) {
      alert(e instanceof Error ? e.message : "Falha na validação");
    } finally {
      setBusy(false);
    }
  }
  async function scan(file?: File) {
    if (!file) return;
    setBusy(true);
    try {
      let value = "";
      const Detector = (window as any).BarcodeDetector;
      const bitmap = await createImageBitmap(file);
      if (Detector) {
        try {
          const codes = await new Detector({ formats: ["qr_code"] }).detect(
            bitmap,
          );
          value = codes[0]?.rawValue || "";
        } catch {}
      }
      if (!value) {
        const scale = Math.min(1, 1600 / Math.max(bitmap.width, bitmap.height));
        const canvas = document.createElement("canvas");
        canvas.width = Math.max(1, Math.round(bitmap.width * scale));
        canvas.height = Math.max(1, Math.round(bitmap.height * scale));
        const context = canvas.getContext("2d", { willReadFrequently: true });
        if (!context) throw new Error("Não foi possível preparar a câmera.");
        context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
        const image = context.getImageData(0, 0, canvas.width, canvas.height);
        value =
          jsQR(image.data, image.width, image.height, {
            inversionAttempts: "attemptBoth",
          })?.data || "";
      }
      bitmap.close();
      if (!value)
        throw new Error(
          "QR Code não reconhecido. Aproxime a câmera e tente novamente.",
        );
      setQ(value);
      await validate(value);
    } catch (e) {
      alert(e instanceof Error ? e.message : "Não foi possível ler o QR Code");
    } finally {
      setBusy(false);
    }
  }
  async function register() {
    if (!result?.allowed || !selected) return;
    const offer = data.partnerOffers.find((x) => String(x.id) === selected);
    try {
      const next = await api({
        action: "use.add",
        member_id: result.member.id,
        partner_id: data.currentPartner?.id,
        offer_id: Number(selected),
        used_at: new Date().toISOString().slice(0, 10),
        detail: offer?.title || "Benefício validado",
        discount_amount: Number(offer?.discount_amount || 0),
      });
      update(next);
      setResult(null);
      setQ("");
      saved("Utilização registrada com sucesso");
    } catch (e) {
      alert(e instanceof Error ? e.message : "Falha ao registrar");
    }
  }
  return (
    <div className="page">
      <Title
        title="Validar associado"
        sub="Confirme a elegibilidade antes de aplicar o benefício."
      />
      <div className="validation-layout">
        <section className="panel scanner-card">
          <h3>Escanear carteirinha</h3>
          <p>Fotografe o QR Code ou digite os dados manualmente.</p>
          <button
            className="qr-target"
            onClick={() => fileRef.current?.click()}
            disabled={busy}
          >
            <span>▦</span>
            <small>
              {busy ? "Lendo QR Code..." : "Toque para usar a câmera"}
            </small>
          </button>
          <input
            ref={fileRef}
            hidden
            type="file"
            accept="image/*"
            capture="environment"
            onChange={async (e) => {
              await scan(e.target.files?.[0]);
              e.currentTarget.value = "";
            }}
          />
          <div className="or">ou digite os dados manualmente</div>
          <label className="field">
            <span>Número da carteirinha ou CPF</span>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && validate()}
              placeholder="Ex.: 0001248"
            />
          </label>
          <button
            className="primary full"
            onClick={() => validate()}
            disabled={busy}
          >
            {busy ? "Validando..." : "Validar associado"}
          </button>
        </section>
        <section className="panel validation-result">
          <h3>Resultado da validação</h3>
          {!result ? (
            <div className="validation-placeholder">
              <div>✓</div>
              <p>
                O resultado aparecerá aqui com somente os dados necessários.
              </p>
            </div>
          ) : (
            <>
              <div
                className={`validation-banner ${result.allowed ? "ok" : "denied"}`}
              >
                {result.allowed ? "Associado válido" : "Benefício indisponível"}
              </div>
              <div className="validated-member">
                <img src={result.member.photo} alt="Foto do associado" />
                <div>
                  <h2>{result.member.name}</h2>
                  <small>Nº da carteirinha</small>
                  <b>{result.member.card_number}</b>
                </div>
              </div>
              <div className="validation-facts">
                <div>
                  <small>Status</small>
                  <span
                    className={`status ${result.allowed ? "green" : "red"}`}
                  >
                    {result.label}
                  </span>
                </div>
                <div>
                  <small>Validade</small>
                  <b>{br(result.member.due_date)}</b>
                </div>
              </div>
              {result.allowed && (
                <>
                  <label className="field">
                    <span>Benefício utilizado</span>
                    <select
                      value={selected}
                      onChange={(e) => setSelected(e.target.value)}
                    >
                      {data.partnerOffers
                        .filter((x) => x.status === "Ativa")
                        .map((x) => (
                          <option key={x.id} value={x.id}>
                            {x.title} — {x.discount_label}
                          </option>
                        ))}
                    </select>
                  </label>
                  <button className="validation-action" onClick={register}>
                    ✓ Registrar utilização
                  </button>
                </>
              )}
              <button className="ghost full" onClick={() => setResult(null)}>
                Cancelar
              </button>
            </>
          )}
        </section>
      </div>
      <div className="panel status-legend">
        <b>Legenda de status</b>
        <span>
          <i className="green" />
          Associado ativo
        </span>
        <span>
          <i className="yellow" />
          Benefício já utilizado
        </span>
        <span>
          <i className="red" />
          Vencido
        </span>
        <span>
          <i className="gray" />
          Cancelado
        </span>
      </div>
    </div>
  );
}

function Offers({ data, edit }: { data: PortalData; edit: (x: Row) => void }) {
  const fresh = {
    title: "",
    description: "",
    image: "/partners/power-fit/gym-cover.webp",
    discount_label: "15%",
    offer_type: "Percentual",
    status: "Ativa",
    valid_until: "2026-12-31",
  };
  return (
    <div className="page">
      <Title
        title="Produtos e descontos"
        sub="Gerencie até cinco ofertas para os associados."
      >
        <button
          className="primary"
          disabled={data.partnerOffers.length >= 5}
          onClick={() => edit(fresh)}
        >
          ＋ Nova oferta
        </button>
      </Title>
      <p className="offer-count">
        <b>{data.partnerOffers.length} de 5 ofertas cadastradas</b>
        <span>
          {data.partnerOffers.length >= 5
            ? "Limite de ofertas atingido"
            : "Você ainda pode cadastrar ofertas"}
        </span>
      </p>
      <section className="offer-admin-list">
        {data.partnerOffers.map((x) => (
          <article className="panel offer-admin" key={x.id}>
            <img src={x.image} alt={x.title} />
            <div className="offer-main">
              <b>{x.title}</b>
              <small>{x.description}</small>
            </div>
            <div>
              <small>Desconto</small>
              <strong>{x.discount_label}</strong>
            </div>
            <div>
              <small>Tipo</small>
              <span>{x.offer_type}</span>
            </div>
            <div>
              <small>Status</small>
              <span className="status green">{x.status}</span>
            </div>
            <button className="ghost" onClick={() => edit(x)}>
              Editar
            </button>
          </article>
        ))}
      </section>
      <div className="info">
        ⓘ Você pode alterar ou desativar ofertas a qualquer momento.
      </div>
    </div>
  );
}

function OfferEditor({
  offer,
  close,
  save,
}: {
  offer: Row;
  close: () => void;
  save: (x: Row) => Promise<void>;
}) {
  const [f, setF] = useState<Row>(offer),
    [busy, setBusy] = useState(false);
  function field(name: string, value: any) {
    setF({ ...f, [name]: value });
  }
  async function upload(file?: File) {
    if (!file) return;
    setBusy(true);
    try {
      const body = new FormData();
      body.append("file", file);
      const r = await fetch("/api/partner-upload.php?kind=offer", {
          method: "POST",
          body,
        }),
        j = await apiJson(r);
      if (!r.ok) throw new Error(j.error);
      field("image", j.url);
    } catch (e) {
      alert(e instanceof Error ? e.message : "Falha no envio");
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="modal-bg">
      <div className="modal offer-modal">
        <div className="modal-head">
          <div>
            <h2>{offer.id ? "Editar oferta" : "Nova oferta"}</h2>
            <p>Cadastre uma imagem e as condições do benefício.</p>
          </div>
          <button onClick={close}>×</button>
        </div>
        <div className="modal-body">
          <div className="offer-edit-grid">
            <div className="offer-image-edit">
              <img
                src={f.image || "/partners/power-fit/gym-cover.webp"}
                alt="Prévia"
              />
              <label className="outline">
                {busy ? "Enviando..." : "Escolher imagem"}
                <input
                  hidden
                  type="file"
                  accept="image/*"
                  onChange={(e) => upload(e.target.files?.[0])}
                />
              </label>
            </div>
            <div className="form-grid">
              <label className="field">
                <span>Nome da oferta *</span>
                <input
                  value={f.title || ""}
                  onChange={(e) => field("title", e.target.value)}
                />
              </label>
              <label className="field">
                <span>Desconto *</span>
                <input
                  value={f.discount_label || ""}
                  onChange={(e) => field("discount_label", e.target.value)}
                />
              </label>
              <label className="field wide">
                <span>Descrição</span>
                <input
                  value={f.description || ""}
                  onChange={(e) => field("description", e.target.value)}
                />
              </label>
              <label className="field">
                <span>Tipo</span>
                <select
                  value={f.offer_type || "Percentual"}
                  onChange={(e) => field("offer_type", e.target.value)}
                >
                  <option>Percentual</option>
                  <option>Preço especial</option>
                  <option>Cortesia</option>
                </select>
              </label>
              <label className="field">
                <span>Validade</span>
                <input
                  type="date"
                  value={f.valid_until || ""}
                  onChange={(e) => field("valid_until", e.target.value)}
                />
              </label>
              <label className="field wide">
                <span>Regras</span>
                <textarea
                  value={f.rules || ""}
                  onChange={(e) => field("rules", e.target.value)}
                />
              </label>
            </div>
          </div>
        </div>
        <div className="modal-foot">
          <button className="ghost" onClick={close}>
            Cancelar
          </button>
          <button
            className="primary"
            disabled={busy}
            onClick={() => {
              if (!f.title || !f.discount_label)
                return alert("Informe o nome e o desconto.");
              save({ action: "partner.offer.save", offer: f });
            }}
          >
            Salvar oferta
          </button>
        </div>
      </div>
    </div>
  );
}

function UsageTable({ rows }: { rows: Row[] }) {
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Data</th>
            <th>Associado</th>
            <th>Oferta utilizada</th>
            <th>Benefício</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((x) => (
            <tr key={x.id}>
              <td>
                {br(x.used_at)} <small>{x.created_at?.slice(11, 16)}</small>
              </td>
              <td>
                <b>{x.member_name}</b>
              </td>
              <td>{x.offer_title || x.detail}</td>
              <td>
                <span className="status green">
                  {x.discount_label || "Validado"}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {!rows.length && (
        <p className="empty">Nenhuma utilização registrada ainda.</p>
      )}
    </div>
  );
}
function Uses({ data }: { data: PortalData }) {
  return (
    <div className="page">
      <Title
        title="Utilizações"
        sub="Histórico dos benefícios utilizados neste estabelecimento."
      />
      <section className="panel partner-table-panel">
        <UsageTable rows={data.uses} />
      </section>
    </div>
  );
}
function Reports({ data }: { data: PortalData }) {
  const discount = data.uses.reduce(
    (s, x) => s + Number(x.discount_amount || 0),
    0,
  );
  return (
    <div className="page">
      <Title
        title="Relatórios"
        sub="Indicadores exclusivos do seu estabelecimento."
      />
      <div className="partner-report-cards">
        <div className="panel">
          <small>Utilizações registradas</small>
          <h2>{data.uses.length}</h2>
          <p>Todos os períodos</p>
        </div>
        <div className="panel">
          <small>Associados atendidos</small>
          <h2>{new Set(data.uses.map((x) => x.member_id)).size}</h2>
          <p>Pessoas diferentes</p>
        </div>
        <div className="panel">
          <small>Descontos concedidos</small>
          <h2>{money(discount)}</h2>
          <p>Economia gerada</p>
        </div>
        <div className="panel">
          <small>Ofertas ativas</small>
          <h2>
            {data.partnerOffers.filter((x) => x.status === "Ativa").length}
          </h2>
          <p>Máximo de cinco</p>
        </div>
      </div>
      <section className="panel partner-table-panel">
        <div className="panel-head">
          <h3>Movimentações recentes</h3>
        </div>
        <UsageTable rows={data.uses.slice(0, 10)} />
      </section>
    </div>
  );
}

function Profile({
  data,
  update,
  saved,
}: {
  data: PortalData;
  update: (x: PortalData) => void;
  saved: (x: string) => void;
}) {
  const [editing, setEditing] = useState(false),
    [f, setF] = useState<Row>(data.currentPartner || {});
  function field(name: string, value: string) {
    setF({ ...f, [name]: value });
  }
  async function upload(file?: File, name = "cover") {
    if (!file) return;
    const body = new FormData();
    body.append("file", file);
    const r = await fetch(`/api/partner-upload.php?kind=partner-${name}`, {
        method: "POST",
        body,
      }),
      j = await apiJson(r);
    if (!r.ok) return alert(j.error);
    field(name, j.url);
  }
  async function save() {
    try {
      update(await api({ action: "partner.profile.save", partner: f }));
      setEditing(false);
      saved("Perfil da empresa atualizado");
    } catch (e) {
      alert(e instanceof Error ? e.message : "Falha ao salvar");
    }
  }
  if (editing)
    return (
      <div className="page">
        <Title
          title="Editar perfil da empresa"
          sub="Essas informações serão exibidas aos associados."
        />
        <section className="panel profile-editor">
          <div className="profile-media-editor">
            <img
              src={f.cover || "/partners/power-fit/gym-cover.webp"}
              alt="Capa"
            />
            <label className="outline">
              Alterar capa
              <input
                hidden
                type="file"
                accept="image/*"
                onChange={(e) => upload(e.target.files?.[0])}
              />
            </label>
          </div>
          <div className="form-grid">
            <label className="field">
              <span>Nome</span>
              <input
                value={f.name || ""}
                onChange={(e) => field("name", e.target.value)}
              />
            </label>
            <label className="field">
              <span>WhatsApp</span>
              <input
                value={f.whatsapp || ""}
                onChange={(e) => field("whatsapp", e.target.value)}
              />
            </label>
            <label className="field wide">
              <span>Descrição</span>
              <textarea
                value={f.description || ""}
                onChange={(e) => field("description", e.target.value)}
              />
            </label>
            <label className="field wide">
              <span>Endereço</span>
              <input
                value={f.address || ""}
                onChange={(e) => field("address", e.target.value)}
              />
            </label>
            <label className="field">
              <span>Horário</span>
              <input
                value={f.hours || ""}
                onChange={(e) => field("hours", e.target.value)}
              />
            </label>
            <label className="field">
              <span>Instagram</span>
              <input
                value={f.instagram || ""}
                onChange={(e) => field("instagram", e.target.value)}
              />
            </label>
            <label className="field">
              <span>Site</span>
              <input
                value={f.website || ""}
                onChange={(e) => field("website", e.target.value)}
              />
            </label>
            <label className="field wide">
              <span>Condições de uso</span>
              <textarea
                value={f.conditions || ""}
                onChange={(e) => field("conditions", e.target.value)}
              />
            </label>
          </div>
          <div className="profile-actions">
            <button className="ghost" onClick={() => setEditing(false)}>
              Cancelar
            </button>
            <button className="primary" onClick={save}>
              Salvar perfil
            </button>
          </div>
        </section>
      </div>
    );
  const p = data.currentPartner!;
  return (
    <div className="page">
      <Title
        title="Perfil público do parceiro"
        sub="Veja como seu perfil aparece para os associados."
      >
        <button className="primary" onClick={() => setEditing(true)}>
          ✎ Editar perfil
        </button>
      </Title>
      <section className="panel public-partner-profile">
        <img
          className="partner-cover"
          src={p.cover || "/partners/power-fit/gym-cover.webp"}
          alt="Ambiente do parceiro"
        />
        <div className="partner-profile-head">
          <div className="partner-public-logo">
            {p.name
              .split(" ")
              .map((x: string) => x[0])
              .slice(0, 2)}
          </div>
          <div>
            <h2>
              {p.name} <span>✓</span>
            </h2>
            <p>{p.description}</p>
          </div>
        </div>
        <div className="partner-public-facts">
          <span>⌖ {p.address}</span>
          <span>◷ {p.hours}</span>
          <span>◉ {p.whatsapp || p.phone}</span>
          <span>◎ {p.instagram}</span>
        </div>
        <h3>Nossos benefícios para associados</h3>
        <div className="public-offers">
          {data.partnerOffers.map((x) => (
            <article key={x.id}>
              <img src={x.image} alt={x.title} />
              <span>{x.discount_label}</span>
              <b>{x.title}</b>
              <p>{x.description}</p>
            </article>
          ))}
        </div>
        <div className="partner-conditions">
          <div>
            <b>Condições de uso</b>
            <p>{p.conditions}</p>
          </div>
        </div>
      </section>
    </div>
  );
}
function Staff({ data }: { data: PortalData }) {
  return (
    <div className="page">
      <Title
        title="Funcionários"
        sub="Pessoas autorizadas a validar associados neste estabelecimento."
      />
      <section className="panel user-panel">
        <div className="panel-head">
          <div>
            <h3>Equipe com acesso</h3>
            <p>O administrador controla a criação e remoção dos acessos.</p>
          </div>
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Funcionário</th>
                <th>Login</th>
                <th>Perfil</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {data.partnerUsers.map((x) => (
                <tr key={x.id}>
                  <td>
                    <b>{x.name}</b>
                    <small className="block">{x.email}</small>
                  </td>
                  <td>
                    <code>{x.username}</code>
                  </td>
                  <td>Parceiro</td>
                  <td>
                    <span className={`status ${x.active ? "green" : "red"}`}>
                      {x.active ? "Ativo" : "Bloqueado"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!data.partnerUsers.length && (
            <p className="empty">Nenhum funcionário vinculado.</p>
          )}
        </div>
      </section>
    </div>
  );
}
